import { ColDef, GridApi, GridOptions, Module } from '@ag-grid-community/core'
import { Component, Input, OnDestroy } from '@angular/core'
import {
  enableHeliosGridDensityLevel,
  HeliosColumnTypes,
  heliosColumnTypes,
} from '@helios/ag-grid-config'
import { DensityLevel, UserPreferencesService } from '@helios/user-preferences'
import { NfColumnTypes } from '@northfork/ag-grid'
import { composeFieldPath } from '@northfork/basics'
import { Observable, Subject } from 'rxjs'
import { takeUntil, tap } from 'rxjs/operators'
import {
  HeliosHttpRequests,
  isHeliosHttpRequestDuplicate,
} from '../../models/http.model'
import { formatBytes, getResponseSize } from './devtools-http-utils'
import {
  DevToolsHttpStatusBarModule,
  devToolsHttpStatusBarPanel,
} from './status-bar/dev-tools-http-status-bar.module'

const field = composeFieldPath([] as HeliosHttpRequests[])

@Component({
  selector: 'hs-dev-tools-http',
  templateUrl: './dev-tools-http.component.html',
  styleUrls: ['./dev-tools-http.component.scss'],
})
export class DevToolsHttpComponent implements OnDestroy {
  gridModules: Module[] = [DevToolsHttpStatusBarModule]
  gridOptions: GridOptions = this.createGridOptions()
  _data: HeliosHttpRequests[] = []
  @Input()
  set httpStream(value: HeliosHttpRequests | null) {
    if (value == null) return
    if (this.gridApi == null) return
    const rowNode = this.gridApi.getRowNode(value.requestId)
    const action = rowNode == null ? 'add' : 'update'
    this.gridApi.applyTransaction({
      [action]: [value],
    })
  }
  @Input()
  set clearHttp(clearData$: Observable<void> | null) {
    if (!clearData$) return
    clearData$
      .pipe(
        takeUntil(this._destroyed$),
        tap(() => this.gridApi.setRowData([])),
      )
      .subscribe()
  }

  gridApi!: GridApi
  private readonly _destroyed$ = new Subject<void>()

  constructor(private readonly preferences: UserPreferencesService) {}

  /**
   * @internal
   */
  createGridOptions(): GridOptions {
    return {
      columnTypes: heliosColumnTypes,
      columnDefs: this.createBaseColumnDefs(),
      defaultColDef: {
        sortable: true,
        filter: true,
      },
      suppressCopyRowsToClipboard: true,
      statusBar: devToolsHttpStatusBarPanel,
      getRowNodeId: data => data.requestId,
      getRowStyle: params => {
        if (params.data.duration > 1000) {
          return { background: 'orange' }
        }

        if (params.data.response?.status >= 400) {
          return { background: 'red' }
        }

        return { background: '' }
      },
      onGridReady: params => {
        this.gridApi = params.api
        this.preferences
          .getDensityLevel()
          .pipe(takeUntil(this._destroyed$))
          .subscribe(level =>
            enableHeliosGridDensityLevel(
              params.api,
              DensityLevel.Compact === level,
            ),
          )
      },
    }
  }

  /**
   * @internal
   */
  createBaseColumnDefs(): ColDef[] {
    return [
      {
        headerName: 'Request ID',
        field: field('requestId'),
        initialHide: true,
      },
      {
        field: field('duration'),
        headerName: 'Duration (ms)',
        type: NfColumnTypes.Integer,
        width: 60,
      },
      {
        field: field('body'),
        headerName: 'Request Body',
        width: 100,
        valueGetter: params => {
          return JSON.stringify(params.data.body ?? {})
        },
      },
      {
        field: field('method'),
        width: 40,
      },
      {
        field: field('url'),
        width: 100,
      },
      {
        field: field('url'),
        headerName: 'path',
        width: 100,
        valueGetter: params => {
          const url = new URL(params.data.url)
          return url.pathname
        },
      },
      {
        field: field('response'),
        valueGetter: params => {
          return JSON.stringify(params.data.response?.body ?? {})
        },
      },
      {
        field: field('url'),
        headerName: 'Status',
        width: 60,
        valueGetter: params => {
          return params.data.response?.status ?? ''
        },
      },
      {
        field: field('url'),
        headerName: 'Source Service',
        valueGetter: params => {
          const url = new URL(params.data.url)
          return url.hostname.split('.')[0].replace('helios-', '')
        },
      },
      {
        field: field('response'),
        headerName: 'Is Duplicate',
        type: HeliosColumnTypes.Boolean,
        sortable: true,
        filter: false,
        valueGetter: params => {
          // Check if there is another request with the same url and method and request body
          const duplicates: HeliosHttpRequests[] = []

          params.api.forEachNode(node => {
            if (isHeliosHttpRequestDuplicate(params.data, node.data))
              duplicates.push(node.data)
          })
          return duplicates.length > 0
        },
      },
      {
        field: field('requestedAt'),
        headerName: 'Requested At',
        type: HeliosColumnTypes.DateTime,
      },
      {
        field: field('completedAt'),
        headerName: 'Completed At',
        type: HeliosColumnTypes.DateTime,
      },
      {
        field: field('response'),
        headerName: 'Response Size',
        valueGetter: params => {
          const byteSize = getResponseSize(params.data.response)
          return formatBytes(byteSize)
        },
      },
    ]
  }

  ngOnDestroy(): void {
    this._destroyed$.next()
    this._destroyed$.complete()
    this.gridApi?.destroy()
  }
}
